import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import style from '../../styles/auth/LoginPage.module.css'
import { useDispatch } from 'react-redux';
import { getLogos } from 'src/redux/reducers/imagesReduser';
import { useSelector } from 'react-redux';
import { RootState } from 'src/interface/IRootState';
import { Card, CardContent, TextField, Button, Typography, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import doneNotify from 'src/notifications/doneNotify';
import errorNotify from 'src/notifications/errorNotify';
import { Bounce, ToastContainer } from 'react-toastify';
import verifyService from 'src/function/auth/verifyService';
import useLogin from 'src/hooks/auth/useLogin';
import AuthService from 'src/function/auth/AuthService';


const validationUserDataSchema = Yup.object({
  email: Yup.string().email('Некоректный email').required("Обязательное поле"),
  password: Yup.string().min(6, 'Минимум 6 символов').required("Обязательное поле"),
});

const validationCodeSchema = Yup.object({
  code: Yup.string().required("Обязательное поле"),
});

const LoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [onSendCode, setOnSendCode] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginUser] = useLogin();

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  useEffect(() => {
    dispatch(getLogos(['p2pLogo']));
  }, [dispatch])

  const selectedLogos = useSelector((state: RootState) => state.images);

  const handleAuth = async (values: { email: string, password: string }) => {
    const { email, password } = values;
    setLoading(true)

    try {
      const isValid = await AuthService.checkCredentials(email, password);

      console.log('is Valid: ', isValid)

      if (!isValid.status) {
        setLoading(false);

        if (isValid.error === 'error check credentials') {
          return errorNotify('Возникла ошибка, попробуйте позже');
        }

        return errorNotify('Неверный email или пароль');
      }

      const sendCode = await verifyService.sendEmailVerifyCode(email);

      if (!sendCode.status) {
        throw new Error('send email error');
      }

      doneNotify('Код подтверждения отправлен вам на почту');
      setUserEmail(email);
      setUserPassword(password);
      setOnSendCode(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      errorNotify('Возникла ошибка при отправке кода, попробуйте позже.');
    }
  }

  const handleSubmitCode = async (values: { code: number }) => {
    try {
      const { code } = values;
      setLoading(true);

      if (!code) {
        return
      }

      const verifyCode = await verifyService.verifyCode(userEmail, code)

      // if (!verifyCode.status && verifyCode.message === 'invalid code') {
      //   setLoading(false);
      //   return errorNotify(' Не верный код подтверждения, попробуйте еще раз');
      // }

      // if (!verifyCode.status) {
      //   throw new Error(verifyCode.message);
      // }

      await loginUser(userEmail, userPassword);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      errorNotify('Возникла непредвиденная ошибка.');
    }
  }

  return (
    <div className={style.container}>
      <div className={style.logoContainer}>
        <img src={selectedLogos.p2pLogo} alt="Logo" className={style.logo} />
      </div>

      <div className={style.content}>
        <h1 className={style.text}>Пожалуйста войдите в свой аккаунт</h1>

        <Card sx={{ maxWidth: 400, margin: 'auto', mt: 5, p: 2 }}>
          {!onSendCode
            ? (<CardContent>
              <Typography variant="h5" align="center" gutterBottom>
                Авторизация
              </Typography>
              <Formik
                key={'login-form'}
                initialValues={{ email: '', password: '' }}
                validationSchema={validationUserDataSchema}
                onSubmit={async (values) => {
                  await handleAuth(values)
                }}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <Field
                      as={TextField}
                      label="Email"
                      name="email"
                      fullWidth
                      margin="normal"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      onChange={handleChange}
                    />
                    <Field
                      as={TextField}
                      label="Пароль"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      fullWidth
                      margin="normal"
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      onChange={handleChange}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={togglePasswordVisibility} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      loading={loading}
                      sx={{ mt: 2 }}>
                      Войти
                    </Button>
                    <Typography variant="body2" align="center" sx={{ mt: 2, textDecoration: 'none' }}>
                      Если у вас нет аккаунта, <Link to="/register">зарегистрируйтесь</Link>
                    </Typography>
                  </Form>
                )}
              </Formik>
            </CardContent>)
            : (<CardContent>
              <Typography variant="h5" align="center" gutterBottom>
                Ведите код подтверждения отправленный на почту {userEmail}
              </Typography>
              <Formik
                key={'code-form'}
                initialValues={{ code: null }}
                validationSchema={validationCodeSchema}
                onSubmit={async (values) => {
                  await handleSubmitCode(values);
                }}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <Field
                      as={TextField}
                      label="Code"
                      name="code"
                      fullWidth
                      margin="normal"
                      error={touched.code && Boolean(errors.code)}
                      helperText={touched.code && errors.code}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      loading={loading}
                    >
                      Войти
                    </Button>
                    <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                      Если у вас нет аккаунта, <Link to="/register">зарегистрируйтесь</Link>
                    </Typography>
                  </Form>
                )}
              </Formik>
            </CardContent>)}
        </Card>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </div>
  )
}

export default LoginPage